const _ = require('underscore');
const stringHelpers = require('./string');
const protocolHelpers = require('./protocol');

const modesHash = {
	prop: 'p',
	crop: 'c',
	attention: 'a',
	entropy: 'e',
	fill: ''
};

const imageFormats = ['avif', 'webp'];

const getBasePath = (_options) => {
	const options = _.defaults({}, _options);
	const { config } = options;
	const protocol = protocolHelpers.get(config);

	let basePath = `${protocol}://${config.host || config.hostname}`;

	if (config.port && !options.dontUsePort) {
		basePath += `:${config.port}`;
	}

	if (config.basePath) {
		basePath += config.basePath;
	}

	return basePath;
};

exports.getBasePath = getBasePath;

const createOldImageUrl = (file, options) => {
	let url =
		getBasePath({ config: options.config.services.storage.old }) +
		file.publicId;

	if (file.type === 'video') {
		if (file.thumbnailIndex) {
			url += `.${file.thumbnailIndex}`;
		} else {
			url += '.5';
		}
	}

	if (options.width || options.height) {
		url += `.${options.width || ''}x${options.height || ''}`;

		let mode;
		if (options.crop === 'fill' && options.gravity in modesHash) {
			mode = options.gravity;
		} else if (options.crop in modesHash) {
			mode = options.crop;
		} else {
			mode = 'prop';
		}

		const { coordinates } = options;

		if (mode !== 'crop' || coordinates) {
			url += modesHash[mode];
		}

		if (mode === 'crop' && coordinates) {
			url += String(coordinates.x) + String(coordinates.y);
		}
	}

	if (options.transform === 'average') {
		url += '.average';
	}

	if (options.format && _.contains(imageFormats, options.format)) {
		url += `.${options.format}`;
	} else {
		url += '.jpg';
	}

	return url;
};

const extensionRegExp = /(?:\.\w+)?$/;
const newImageUrlParams = [
	'x',
	'y',
	'width',
	'height',
	'transform',
	'crop', // alias for transform
	'gravity',
	'quality',
	'blur'
];

const createNewImageUrl = (file, _options) => {
	const options = _.defaults({}, _options);

	const basePath = getBasePath({
		config: options.config.services.storage.main,
		dontUsePort: options.dontUsePort
	});

	const originalImagePrefix = file.acl ? 'files' : 'original-images';
	const prefix = options.original ? originalImagePrefix : 'images';

	let url = [basePath, prefix, file.publicId].join('/');

	if (options.original) {
		return url;
	}

	// _ is not a function in next.js projects because of ESM
	const imageParams = _.chain(options.params)
		.pick(newImageUrlParams)
		.omit(_.has(options.params, 'transform') ? 'crop' : [])
		.map((value, param) => value && `${param[0]}_${value}`)
		.compact()
		.value();

	let { format } = options;
	if (!format && options.params && options.params.transform === 'average') {
		// png is significantly better at storing single color images
		// then most other legacy image formats
		format = 'png';
	}

	if (format || imageParams.length > 0) {
		let name = options.name || file.originalName;
		name = stringHelpers.transliterate(name, { custom: { '.': '.' } });

		if (format) {
			name = name.replace(extensionRegExp, `.${format}`);
		}

		const paramsString = imageParams.join(',') || '_';
		url += `/${paramsString}/${name}`;
	}

	return url;
};

const videoRegExp = /^\/file\/start\//;
const streamRegExp = /^\/live\//;

const isNewVideo = (file) => {
	return file.type === 'video' && videoRegExp.test(file.publicId);
};

const isStream = (file) => {
	return file.type === 'video' && streamRegExp.test(file.publicId);
};

exports.getImageUrl = (file, options) => {
	let url;

	if (file.type === 'video' && file.previews) {
		let config;

		if (streamRegExp.test(file.publicId)) {
			config = options.config.services.eipsk.stream;
		} else {
			config = options.config.services.video;
		}

		const basePath = getBasePath({ config });
		const previewIndex = file.previewIndex || 0;
		url = basePath + file.previews[previewIndex];
	} else if (file.averageColor) {
		url = createNewImageUrl(file, options);
	} else {
		url = createOldImageUrl(
			file,
			_.chain(options).omit('params').extend(options.params).value()
		);
	}

	return url;
};

const ensureTrailingSlash = (url = '') => {
	return url.charAt(url.length - 1) === '/' ? url : `${url}/`;
};

const filenameRegExp = /^[^/]+\.[a-z]+$/i;
const transformParamRegExp = /^(?:[ct]\w*)?_/i;

exports.patchImageUrl = (originalUrl, options) => {
	const { transform } = options;
	let { format } = options;
	let url = transform ? options.fallbackUrl || originalUrl : originalUrl;

	if (typeof originalUrl !== 'string') {
		// invalid originalUrl value
		return url;
	}

	if (!transform && !_.contains(imageFormats, format)) {
		// nothing to patch
		return url;
	}

	const storageConfig = options.config.services.storage;
	const mainBasePath = getBasePath({ config: storageConfig.main });
	const oldBasePath = getBasePath({ config: storageConfig.old });
	const urlObject = new URL(originalUrl, mainBasePath);

	if (urlObject.search || urlObject.hash) {
		// unexpected url params
		return url;
	}

	const host = urlObject.hostname;
	const path = urlObject.pathname;
	let pathParts = [];
	const mainHost = new URL(mainBasePath).hostname;
	const oldHost = new URL(oldBasePath).hostname;
	const mainPrefix = `${ensureTrailingSlash(
		storageConfig.main.basePath
	)}images/`;
	const oldPrefix = ensureTrailingSlash(storageConfig.old.basePath);

	if (host === mainHost && path.indexOf(mainPrefix) === 0) {
		pathParts = path.slice(mainPrefix.length).split('/');
		format = format || 'png';

		if (
			pathParts.length === 4 &&
			filenameRegExp.test(pathParts[1]) &&
			filenameRegExp.test(pathParts[3])
		) {
			// full image url with params and custom name
			if (transform) {
				const basePart = `${pathParts[0]}/${pathParts[1]}`;
				const paramsPart = pathParts[2]
					.split(',')
					.filter((param) => !transformParamRegExp.test(param))
					.concat(`t_${transform}`)
					.join(',');
				const namePart = pathParts[3].replace(extensionRegExp, `.${format}`);

				url = `${mainPrefix}${basePart}/${paramsPart}/${namePart}`;
			} else {
				url = originalUrl.replace(extensionRegExp, `.${format}`);
			}
		} else if (pathParts.length === 2 && filenameRegExp.test(pathParts[1])) {
			// short image url without name and params
			const paramsString = transform ? `t_${transform}` : '_';
			url = `${originalUrl}/${paramsString}/img.${format}`;
		}
	} else if (host === oldHost && path.indexOf(oldPrefix) === 0) {
		pathParts = path.slice(oldPrefix.length).split('/');
		format = format || 'jpg';

		if (
			pathParts.length === 1 &&
			filenameRegExp.test(pathParts[0]) &&
			pathParts[0].slice(-4) === '.jpg'
		) {
			// old storage url
			url = originalUrl.replace(extensionRegExp, '');

			if (transform === 'average' && url.slice(-8) !== '.average') {
				url += '.average';
			}

			url += `.${format}`;
		}
	}

	return url;
};

exports.getBinaryFileUrl = (file, _options) => {
	const options = _.defaults({}, _options);

	let url;
	let name;
	let basePath;

	const storageConfig = options.config.services.storage;

	if (file.acl || file.folder) {
		basePath = getBasePath({ config: storageConfig.main });
		name = options.name || file.originalName || '';

		if (file.acl) {
			url = `${basePath}/files/${file.publicId}`;
		} else {
			url = `${basePath}/documents/${file.publicId}`;
		}

		if (name) {
			url += `/${name}`;
		}
	} else {
		const extension = _.last(file.originalName.split('.'));

		basePath = getBasePath({ config: storageConfig.old });
		name = options.name ? `${options.name}.${extension}` : file.originalName;

		url = `${basePath + file.publicId}/${name}`;
	}

	return url;
};

exports.getAudioFileUrl = (file, _options) => {
	const options = _.defaults({}, _options);

	let url;
	let basePath;

	if (file.acl || file.folder) {
		const name = options.name || file.originalName || '';
		basePath = getBasePath({ config: options.config.services.storage.main });

		if (file.acl) {
			url = `${basePath}/files/${file.publicId}`;
		} else {
			url = `${basePath}/audio/${file.publicId}`;
		}

		if (name) {
			url += `/${name}`;
		}
	} else {
		basePath = getBasePath({ config: options.config.services.storage.old });
		url = `${basePath + file.publicId}.1.mp3`;
	}

	return url;
};

exports.getVideoFileUrl = (file, options) => {
	let url;
	let basePath;

	if (isStream(file)) {
		basePath = getBasePath({ config: options.config.services.eipsk.stream });
		url = basePath + file.publicId;
	} else if (isNewVideo(file)) {
		basePath = getBasePath({ config: options.config.services.video });
		url = basePath + file.publicId;
	} else {
		basePath = getBasePath({ config: options.config.services.storage.old });
		url = `${basePath + file.publicId}.360p.${options.format}`;
	}

	return url;
};

exports.getFileExtension = (fileName) => {
	return fileName.split('.').pop().slice(1);
};

exports.getVideoFormats = (file) => {
	const formats = ['mp4', 'webm'];

	if (isNewVideo(file) || isStream(file)) {
		formats.unshift('m3u8');
	}

	return formats;
};

const videoFormat2ContentType = {
	m3u8: 'application/x-mpegURL',
	mp4: 'video/mp4',
	webm: 'video/webm'
};

exports.getVideoSource = function getVideoSource(file, options) {
	return {
		src: this.getFileUrl(file, options),
		type: videoFormat2ContentType[options.format]
	};
};
